import {CAMPAIGN_KEYS, useCampaignQuery} from "@/api/campaignApi";
import IntercomCampaignSync from "@/components/Campaign/IntercomCampaignSync";
import CampaignManagement from "@/components/Campaign/management/CampaignManagement";
import CampaignTabs from "@/components/Campaign/management/CampaignTabs";
import CampaignOverview from "@/components/Campaign/management/campaignOverview/CampaignOverview";
import CampaignHeader from "@/components/Campaign/management/campaignOverview/header/CampaignHeader";
import FullPageLoader from "@/components/Common/FullPageLoader";
import NotFound from "@/components/Common/NotFound";
import type {CampaignManagementSerializer, TokenClaimsSerializer} from "@/generated/DjangoTypes";
import {useLogout} from "@/hooks/useLogout";
import {usePosthogSurvey} from "@/hooks/usePosthogSurvey";
import {useIsAdmin, useSelectedCompany} from "@/hooks/useUserHooks";
import {PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import {useCampaignGalleryStore} from "@/store/campaignGallery";
import useCampaignManagementStore, {useManagedCampaignType, useManagementCampaign} from "@/store/campaignManagement";
import {useCarouselStore} from "@/store/carousel";
import {requestTakedownModalAtom, reviewContentModalAtom, selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import useTokenStore from "@/store/token";
import {fetchCampaign} from "@/utils/loaderUtils";
import {createFileRoute, redirect} from "@tanstack/react-router";
import {zodValidator} from "@tanstack/zod-adapter";
import type {AxiosResponse} from "axios";
import {getDefaultStore, useSetAtom} from "jotai";
import {jwtDecode} from "jwt-decode";
import isEqual from "lodash/isEqual";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useUnmount} from "react-use";
import {z} from "zod";

export const Route = createFileRoute("/campaign/$campaignId/")({
  component: CampaignDetails,
  params: {
    parse: raw => ({campaignId: +raw.campaignId}),
    stringify: params => ({campaignId: params.campaignId?.toString()}),
  },
  validateSearch: zodValidator(
    z.object({
      content_takedown: z.boolean().optional(),
      name: z.string().optional(),
      show_survey: z.boolean().optional(),
      submission_id: z.number().optional(),
      tab: z.any().optional(),
    }),
  ),
  loader: async ({params}) => {
    try {
      const token = useTokenStore.getState().access;
      const campaign = await fetchCampaign(params.campaignId);
      const campaignStore = useCampaignManagementStore.getState();

      if (campaignStore.campaign.id !== params.campaignId) {
        const cachedManagement = queryClient.getQueryData<AxiosResponse<CampaignManagementSerializer>>(
          CAMPAIGN_KEYS.qCampaignManagement(params.campaignId),
        );

        campaignStore.setValue("campaign", campaign);
        if (cachedManagement) {
          campaignStore.setValue("management", cachedManagement.data);
        }
      }

      if (!token) {
        return {campaign};
      }

      const claims = jwtDecode<TokenClaimsSerializer>(token);
      const companyId = campaign.company_profile?.id;
      const hasAccessToSubBrand = claims.sub_brand_ids?.includes(companyId!);
      const isAgency = claims.access_level === "AGENCY";

      if (isAgency && hasAccessToSubBrand && !!companyId) {
        getDefaultStore().set(selectedCompanyIdAtom, companyId);
      }

      return;
    } catch (error) {
      console.log("Error loading campaign:", error);
      throw redirect({to: "/login", replace: true});
    }
  },
});

function CampaignDetails() {
  const {t} = useTranslation("campaign_overview");
  const navigate = Route.useNavigate();
  const {campaignId} = Route.useParams();
  const {content_takedown, show_survey, submission_id} = Route.useSearch();
  const setRequestTakedownModal = useSetAtom(requestTakedownModalAtom);
  const setReviewContentModal = useSetAtom(reviewContentModalAtom);
  const campaign = useManagementCampaign();
  const campaignType = useManagedCampaignType();
  const isAdmin = useIsAdmin();
  const logout = useLogout();
  const selectedCompany = useSelectedCompany();
  const setPersistedMediaIndex = useCarouselStore(state => state.setCurrentIndex);
  const setValue = useCampaignManagementStore(state => state.setValue);
  const user_id = useTokenStore(state => state.userId);

  const {data, error, isError, isLoading} = useCampaignQuery({
    campaignId: Number(campaignId),
    queryOptions: {
      queryKey: [...CAMPAIGN_KEYS.qCampaign(Number(campaignId)), "detail"],
      refetchOnMount: true,
    },
  });

  const canUserManageCampaign = selectedCompany?.id === campaign.company_profile?.id || isAdmin;
  const isDraftOrPlannedCampaign = campaign.status! === "draft" || campaign.status! === "planned";

  usePosthogSurvey(
    PostHogEvents.SurveyAfterCampaignCreationAUG2025,
    {campaign_id: campaignId, campaign_type: campaignType},
    !!show_survey && !!campaignType && !!campaignId,
  );

  useUnmount(() => {
    setPersistedMediaIndex(0);
    useCampaignGalleryStore.getState().reset();
    useCampaignManagementStore.getState().reset();
  });

  useEffect(() => {
    if (!submission_id) {
      return;
    }

    if (content_takedown && isAdmin) {
      setRequestTakedownModal(submission_id);
      return;
    }

    setReviewContentModal(state => {
      state.isOpen = true;
      state.submissionId = submission_id;
    });
  }, [submission_id, content_takedown, isAdmin]);

  useEffect(() => {
    if (!user_id && ((!!campaign && !isEqual(campaign, {}) && !campaign.is_public) || isError)) {
      navigate({to: "/login"});
    }

    if (error?.response?.status === 401 || error?.response?.status === 403) {
      logout();
    }
  }, [isError, user_id, campaign]);
  useEffect(() => {
    if (data) {
      setValue("campaign", data);
    }
  }, [data, setValue]);

  if (isError || (!canUserManageCampaign && isDraftOrPlannedCampaign)) {
    return <NotFound message={t("campaign_not_found")} page={t("campaigns")} redirect="/campaigns" />;
  }

  if (isLoading) {
    return <FullPageLoader />;
  }

  return (
    <IntercomCampaignSync>
      <meta name="robots" content="noindex" />
      <title>{(campaign.title || t("draft")) + " - Refluenced"}</title>
      {!!campaign.title && <meta property="og:title" content={campaign.title} />}
      {!!campaign.long_description && <meta property="og:description" content={campaign.long_description} />}
      {!!campaign.picture && <meta property="og:image" content={campaign.picture} />}
      <div className="max-w-wide container mx-auto mt-8 mb-20 px-8">
        <CampaignHeader />
        {canUserManageCampaign && campaign.status! !== "draft" && <CampaignTabs />}
        {canUserManageCampaign ? <CampaignManagement /> : <CampaignOverview />}
      </div>
    </IntercomCampaignSync>
  );
}
